/**
 * RightPanel Component - CORE genérico
 *
 * @version 2.1
 * @updated 2026-02-25
 * @module core/layouts/panels
 *
 * @description
 * Panel lateral derecho permanente que acepta contenido inyectable de módulos.
 * Por defecto está OCULTO, y solo aparece cuando se selecciona un elemento.
 *
 * @remarks
 * **IMPORTANTE**: Este panel es PERMANENTE y parte del CORE.
 * Los módulos inyectan su contenido mediante la prop `children`.
 *
 * **Comportamiento**:
 * - Oculto por defecto (translateX(100%))
 * - Aparece automáticamente cuando `feature` no es null
 * - Se oculta al hacer clic en botón de cierre flotante (chevron >>)
 * - Ancho fijo: 360px (escalable con var(--ui-scale))
 * - Transición suave con translateX(100%)
 *
 * **Estructura**:
 * - Botón flotante de cierre (chevron >> apuntando a la derecha)
 * - Content: Vacío, inyectable por módulos mediante `children`
 *
 * @example
 * ```tsx
 * <RightPanel feature={selectedFeature} onClose={clearSelection}>
 *   {activeModule === 'environmental' && <EnvironmentalRightContent feature={selectedFeature} />}
 * </RightPanel>
 * ```
 */

import { useEffect, useState } from 'react';
import './right-panel.css';

/**
 * Props del componente RightPanel
 */
export interface RightPanelProps {
  /**
   * Elemento seleccionado en el mapa (null = panel oculto)
   */
  feature?: any | null;

  /**
   * Callback cuando el usuario cierra el panel
   */
  onClose?: () => void;

  /**
   * Callback cuando el panel cambia de estado (visible/hidden)
   * @param visible - true si el panel está visible
   */
  onToggle?: (visible: boolean) => void;

  /**
   * Contenido a renderizar dentro del panel (inyectado por módulo)
   */
  children?: React.ReactNode;
}

/**
 * Componente RightPanel
 *
 * @remarks
 * Panel lateral derecho con contenido contextual según el elemento seleccionado.
 * Siempre renderizado en el DOM, pero oculto por defecto.
 */
export function RightPanel({ feature, onClose, onToggle, children }: RightPanelProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false);

  /**
   * Actualiza visibilidad cuando cambia el feature
   */
  useEffect(() => {
    const visible = feature !== null && feature !== undefined;
    setIsVisible(visible);
    onToggle?.(visible);
  }, [feature, onToggle]);

  /**
   * Cierra el panel
   */
  const handleClose = (): void => {
    onClose?.();
  };

  /**
   * Genera clases CSS dinámicas
   */
  const getPanelClasses = (): string => {
    const classes = ['right-panel'];
    if (!isVisible) classes.push('hidden');
    return classes.join(' ');
  };

  return (
    <aside
      className={getPanelClasses()}
      role="complementary"
      aria-label="Panel de detalles"
      aria-hidden={!isVisible}
      data-visible={isVisible}
    >
      {/* Botón flotante de cierre (visible solo cuando el panel está abierto) */}
      {isVisible && (
        <button
          type="button"
          className="right-panel-toggle-floating"
          onClick={handleClose}
          aria-label="Cerrar panel"
          title="Cerrar panel"
        >
          {/* Símbolo >> de WESTEER (apunta derecha para cerrar) */}
          <svg
            viewBox="0 0 88 88"
            fill="currentColor"
            className="floating-icon"
            aria-hidden="true"
          >
            {/* Primer chevron - muy grueso */}
            <polygon points="0,5 34,40 0,75 22,75 56,40 22,5" />
            {/* Segundo chevron - muy grueso */}
            <polygon points="32,5 66,40 32,75 54,75 88,40 54,5" />
          </svg>
        </button>
      )}

      {/* Contenido principal - vacío por defecto */}
      <div className="right-panel-content">
        {children}
      </div>
    </aside>
  );
}
